/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useState } from "react";
import { AccountContext, ContextUpdater } from "../context/ContextProvider";
import { Input, Label } from "reactstrap";
import { requestCaptivePortalLayout } from "../context/RequestManager";
import {
  navigateToAccount,
  navigateToCaptivePortal,
  navigateToClient,
  navigateToLocation,
  navigateToMarketing,
  navigateToRouter
} from "../util/navigation";
import DismissablePopover from "./DismissablePopover";
import GridContainer from "./GridContainer";
import ItemLabel from "../util/Icons";
import useUpdateState from "../util/useUpdateState";
import ModalItemPreview from "../pages/admin/ModalItemPreview";
import debug from "../util/constants/Debug";

// TODO: Implement client-only version
const SearchAnythingBar = ({ className, admin = false }) => {
  // constants
  const id = "smartwifi-search-anything-bar";
  const maxFilterItems = 60;

  // context
  const updateContexts = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);

  // state
  const [detailState, , updateDetailState] = useUpdateState({ open: false, type: null, item: null });

  const {
    adminClientList: clients,
    adminLocationList: locations,
    adminRouterList: routers,
    adminAccountList: accounts,
    adminTemplateList: templates,
    adminCaptivePortalLayoutList: captivePortals,
    adminMarketingList: marketingList,
  } = accountContext;

  const searchableItems = [
    { type: "client", list: clients },
    { type: "location", list: locations },
    { type: "router", list: routers.map(router => ({ ...router, name: router.macAddress })) },
    { type: "account", list: accounts },
    { type: "template", list: templates },
    { type: "captive-portal", list: captivePortals },
    { type: "marketing", list: marketingList },
  ];
  const searchableItemNames = {
    "client": "Business",
    "location": "Location",
    "router": "Router",
    "account": "Account",
    "template": "Template",
    "captive-portal": "Login Page",
    "marketing": "Marketing",
  };

  // seach term
  const [searchTerm, setSearchTerm] = useState("");
  const [inputFocus, setInputFocus] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // handlers
  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    if (searchTerm.length < newSearchTerm.length) {
      setPopoverOpen(true);
    }
    setSearchTerm(newSearchTerm);
  };

  const handleItemSelected = (item) => {
    // dismiss popover
    setPopoverOpen(false);

    // handle updating state
    const type = item?.type;
    const client = { id: item.clientId, name: item.clientName };
    switch (type) {
      case "client":
        navigateToClient({
          contextUpdater: updateContexts,
          accountContext: accountContext,
          client: item
        });
        break;
      case "location":
        navigateToLocation({
          contextUpdater: updateContexts,
          accountContext: accountContext,
          client: client,
          locationId: item.id
        });
        break;
      case "router":
        navigateToRouter({
          contextUpdater: updateContexts,
          accountContext: accountContext,
          client: client,
          router: item
        });
        break;
      case "captive-portal":
        if (item.clientId) {
          navigateToCaptivePortal({
            contextUpdater: updateContexts,
            accountContext: accountContext,
            client: client,
            captivePortal: item
          });
        } else {
          // TODO: Find a different way to handle global login pages (probably need its own page)
          requestCaptivePortalLayout({
            context: updateContexts,
            token: accountContext.token,
            layoutId: item.id.toString(),
            layoutKey: "initialLayout",
            showLoading: true,
            completion: (results) => {
              // check for error
              debug.log(results);
              const config = results.captivePortal?.initialLayout?.config;

              if (!config) {
                alert("Error previewing layout");
                return;
              }

              // update state
              updateDetailState({ open: true, type: "captive-portal", item: { ...item, config } });
            }
          });
        }
        break;
      case "account":
        navigateToAccount({
          contextUpdater: updateContexts,
          accountContext: accountContext,
          client: client,
          account: item
        });
        break;
      case "marketing":
        navigateToMarketing({
          contextUpdater: updateContexts,
          accountContext: accountContext,
          client: client,
          item: item
        });
        break;
      default:
        debug.warn("Invalid item type: " + type);
        break;
    }
  };

  // check if data is loaded
  if (searchableItems.some(item => !Array.isArray(item.list))) {
    return null;
  }

  // searchable data
  const totalData = [];
  const lowerCaseSearchTerm = searchTerm.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
  searchableItems.forEach((section) => {
    section.list.forEach((item) => {
      totalData.push({ ...item, type: section.type, name: item.name });
    });
  });
  const filteredData = !searchTerm ? null : totalData.filter(item => {
    return item.name?.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").includes(lowerCaseSearchTerm);
  }).sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return 0;
  });

  // get popover details
  const getPopoverDetails = (filteredData) => {
    const count = filteredData?.length;

    if (count == null) {
      return <Label className="w-100 text-center text-muted" size="lg">Start typing to search...</Label>;
    }
    else if (count === 0) {
      return <Label className="w-100 text-center text-muted" size="lg">No items found...</Label>;
    }
    else if (count > maxFilterItems) {
      return <Label className="w-100 text-center text-muted" size="lg">Continue typing to narrow search...</Label>;
    }
    else {
      return (
        <GridContainer width="250" style={{ marginTop: "10px" }}>
          {filteredData?.map((item, index) => (
            <div
              key={index}
              className="d-flex flex-column justify-content-center align-items-center hoverable-item"
              style={{ border: "1px solid #666", fontSize: "16px" }}
              onClick={() => handleItemSelected(item)}
            >
              <ItemLabel className="no-wrap no-spacing fw-bold" type={item.type}>
                {searchableItemNames[item.type]}
              </ItemLabel>
              <Label className="w-100 text-center no-wrap no-spacing">
                {item.name}
                {item.clientName && <span className="text-muted"> - {item.clientName}</span>}
                {item.type === "captive-portal" && !item.clientName && <span> - <i className="bi-globe2" /></span>}
              </Label>
            </div>
          ))}
        </GridContainer>
      );
    }
  };

  return (
    <>
      <Input
        className={className}
        id={id}
        placeholder="Search for anything..."
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={() => setInputFocus(true)}
        onBlur={() => setInputFocus(false)}
        autoComplete="off"
        style={{ zIndex: "50" }} 
      />
      <DismissablePopover
        className="popover-large"
        placement="bottom"
        isOpen={popoverOpen}
        toggle={() => setPopoverOpen(prev => inputFocus || !prev)}
        target={id}
        zIndex="49"
      >
        <div className="my-1 mx-2" style={{ maxWidth: "70vw", width: "900px" }}>
          {getPopoverDetails(filteredData)}
        </div>
      </DismissablePopover>
      <ModalItemPreview
        type={detailState.type}
        isOpen={detailState.open}
        item={detailState.item}
        onDone={() => updateDetailState({ open: false })}
      />
    </>
  );
};

export default SearchAnythingBar;
